import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, AlertCircle, Bell, VolumeX } from 'lucide-react';
import { useGetArbSensorsQuery, useAssignArbSensorMutation, useRemoveArbSensorMutation, useUpdateArbSettingsMutation, useGetArbSettingsQuery } from '../../services/baseUnitApi';
import { useGetSensorListQuery } from '../../services/sensorApi';

interface ArbSensorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  baseUnit: {
    serialNo: number;
    base_unit_name: string;
    silent_alarm?: boolean;
  } | null;
}

export default function ArbSensorDialog({ isOpen, onClose, baseUnit }: ArbSensorDialogProps) {
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [silentAlarm, setSilentAlarm] = useState(baseUnit?.silent_alarm ?? false);

  const arbSerial = baseUnit != null ? String(baseUnit.serialNo).trim() : '';

  const { data: arbSettings } = useGetArbSettingsQuery(arbSerial, { skip: !isOpen || !arbSerial });

  // Sync local state from fresh server data whenever the dialog opens or the GET response arrives
  useEffect(() => {
    if (arbSettings !== undefined) {
      setSilentAlarm(arbSettings.silent_alarm);
    } else {
      setSilentAlarm(baseUnit?.silent_alarm ?? false);
    }
  }, [arbSettings, isOpen]);

  const { data: assigned = [], isLoading: loadingAssigned, isFetching: fetchingAssigned } = useGetArbSensorsQuery(arbSerial, {
    skip: !isOpen || !arbSerial,
    refetchOnMountOrArgChange: true,
  });
  const { data: allSensors = [], isLoading: loadingSensors } = useGetSensorListQuery(undefined, {
    skip: !isOpen,
  });

  const [updateArbSettings] = useUpdateArbSettingsMutation();
  const [assignSensor] = useAssignArbSensorMutation();
  const [removeSensor] = useRemoveArbSensorMutation();

  const assignedIds = new Set(assigned.map(s => Number(s.sensor_id)));
  const unassigned = allSensors.filter(s => !assignedIds.has(Number(s.id)) && !s.is_deleted);

  const handleToggleSilentAlarm = async () => {
    if (!arbSerial) return;
    const newValue = !silentAlarm;
    setSilentAlarm(newValue);
    try {
      await updateArbSettings({ arbSerial, silent_alarm: newValue }).unwrap();
    } catch {
      setSilentAlarm(!newValue);
      setError('Failed to update silent alarm setting. Please try again.');
    }
  };

  const handleAssign = async (sensorId: number) => {
    setError(null);
    const sensor = allSensors.find(s => s.id === sensorId);
    try {
      await assignSensor({
        arbSerial,
        sensorId,
        sensorName: sensor?.name,
        sensorSerialNo: sensor?.serialNo,
      }).unwrap();
      setShowAdd(false);
    } catch {
      setError('Failed to assign sensor. Please try again.');
    }
  };

  const handleRemove = async (sensorId: number) => {
    setError(null);
    try {
      await removeSensor({ arbSerial, sensorId }).unwrap();
    } catch {
      setError('Failed to remove sensor. Please try again.');
    }
  };

  if (!isOpen || !baseUnit) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-app-panel dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Bell className="h-5 w-5 text-indigo-500" />
              ARB Sensor Assignment
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {baseUnit.base_unit_name} · Serial {baseUnit.serialNo}
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-4 flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-md px-3 py-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="mb-4 flex items-center justify-between p-3 rounded-md bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-2">
              <VolumeX className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Silent alarm</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Show alarm on screen but suppress buzzer and relay output</p>
              </div>
            </div>
            <button
              onClick={handleToggleSilentAlarm}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                silentAlarm ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
              role="switch"
              aria-checked={silentAlarm}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                  silentAlarm ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Only sensors assigned here will trigger alarms on this ARB unit.
          </p>

          {loadingAssigned ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">Loading assigned sensors...</div>
          ) : assigned.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">No sensors assigned yet.</p>
          ) : (
            <ul className="space-y-1 mb-4">
              {assigned.map(s => (
                <li key={s.sensor_id} className="flex items-center justify-between px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/50">
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{s.sensor_name}</span>
                    <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 font-mono">{s.sensor_serialNo}</span>
                  </div>
                  <button
                    onClick={() => handleRemove(s.sensor_id)}
                    className="p-1 text-red-400 hover:text-red-600 dark:hover:text-red-300"
                    title="Remove from ARB"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {showAdd ? (
            <div className="border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Select sensor to add</span>
                <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {loadingSensors ? (
                <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">Loading sensors...</div>
              ) : unassigned.length === 0 ? (
                <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 italic">All sensors are already assigned.</div>
              ) : (
                <ul className="max-h-52 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-600">
                  {unassigned.map(s => (
                    <li key={s.id}>
                      <button
                        onClick={() => handleAssign(s.id)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 dark:hover:bg-indigo-900/30 flex items-center justify-between"
                      >
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{s.name}</div>
                          <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">{s.serialNo}</div>
                        </div>
                        <Plus className="h-4 w-4 text-indigo-500" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <button
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium"
            >
              <Plus className="h-4 w-4" />
              Add sensor
            </button>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
